import { useState, useEffect } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
const Navigation = () => {
  const location = useLocation();
  const params = useParams();
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [workOpen, setWorkOpen] = useState(false);
  const [workCategories, setWorkCategories] = useState<Array<{ name: string; path: string }>>([]);
  const [currentJobTitle, setCurrentJobTitle] = useState<string | null>(null);
  const [currentCategory, setCurrentCategory] = useState<{ name: string; slug: string } | null>(null);
  const [pageTitle, setPageTitle] = useState<string | null>(null);

  const isActive = (path: string) => location.pathname === path;

  useEffect(() => {
    const loadCategories = async () => {
      const { data } = await supabase.from("categories").select("name, slug").order("sort_order");

      if (data) {
        const categories = data
          .filter((cat) => cat.slug !== "hej-mamma")
          .map((cat) => ({
            name: cat.name,
            path: `/work/${cat.slug}`,
          }));
        setWorkCategories(categories);
      }
    };

    loadCategories();
  }, []);

  useEffect(() => {
    const loadJobTitle = async () => {
      if (params.job) {
        const { data } = await supabase
          .from("jobs")
          .select("title, category_id, categories(name, slug)")
          .eq("slug", params.job)
          .maybeSingle();

        setCurrentJobTitle(data?.title || null);
        setCurrentCategory(data?.categories ? { name: data.categories.name, slug: data.categories.slug } : null);
        setPageTitle(null);
      } else if (params.category) {
        // We're on a category page
        const { data } = await supabase
          .from("categories")
          .select("name, slug")
          .eq("slug", params.category)
          .maybeSingle();

        if (data) {
          setPageTitle(data.name);
          setCurrentCategory(null);
          setCurrentJobTitle(null);
        }
      } else {
        // Check if we're on About or Hej Mamma pages
        if (location.pathname === "/about") {
          setPageTitle("About");
        } else if (location.pathname === "/hej-mamma") {
          setPageTitle("Hej Mamma");
        } else if (location.pathname === "/brollopsfotograf") {
          setPageTitle("Bröllop");
        } else {
          setPageTitle(null);
        }
        setCurrentJobTitle(null);
        setCurrentCategory(null);
      }
    };

    loadJobTitle();
  }, [params.job, params.category, location.pathname]);
  const NavLink = ({ to, children }: { to: string; children: React.ReactNode }) => {
    const active = isActive(to);
    const hovered = hoveredItem === to;
    return (
      <Link
        to={to}
        className="relative text-sm tracking-wide transition-colors font-sans font-medium"
        onMouseEnter={() => setHoveredItem(to)}
        onMouseLeave={() => setHoveredItem(null)}
      >
        {children}
        {(active || hovered) && <span className="absolute -bottom-1 left-0 right-0 h-px bg-accent" />}
      </Link>
    );
  };
  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background">
      <div className="container mx-auto px-4 sm:px-8 max-w-6xl py-8">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <Link
              to="/"
              className="text-xl md:text-3xl tracking-widest text-foreground hover:text-accent transition-colors"
              style={{ fontFamily: '"Schibsted Grotesk", sans-serif', fontWeight: 500 }}
            >
              INA NEDERDAL
            </Link>
          </div>

          {/* Job Title */}
          {currentJobTitle && (
            <div className="ml-8">
              <div className="hidden md:flex items-baseline gap-2 text-sm tracking-wide text-muted-foreground">
                {currentCategory && (
                  <>
                    <Link
                      to={`/work/${currentCategory.slug}`}
                      className="transition-colors hover:text-foreground"
                    >
                      {currentCategory.name}
                    </Link>
                    <span aria-hidden="true">/</span>
                  </>
                )}
                <h1 className="font-normal">{currentJobTitle}</h1>
              </div>
              <h1 className="sr-only">{currentJobTitle}</h1>
            </div>
          )}

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-baseline gap-8 ml-auto">
            <NavLink to="/">Home</NavLink>

            <div className="relative" onMouseEnter={() => setWorkOpen(true)} onMouseLeave={() => setWorkOpen(false)}>
              <button
                className="relative text-sm tracking-wide transition-colors font-sans font-medium bg-transparent border-0 p-0 cursor-pointer text-foreground hover:text-accent"
                onMouseEnter={() => setHoveredItem("/work")}
                onMouseLeave={() => setHoveredItem(null)}
              >
                Work
                {(hoveredItem === "/work" || workOpen) && (
                  <span className="absolute -bottom-1 left-0 right-0 h-px bg-accent" />
                )}
              </button>

              <div
                className={`absolute left-1/2 -translate-x-1/2 top-full pt-4 transition-all duration-200 ${workOpen ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-2"}`}
              >
                <div className="bg-background/95 backdrop-blur-md border border-border/40 shadow-lg p-4 w-48">
                  <ul className="flex flex-col gap-1">
                    {workCategories.map((category) => (
                      <li key={category.path}>
                        <Link
                          to={category.path}
                          className="block px-4 py-2 text-sm tracking-wide hover:text-accent transition-colors"
                        >
                          {category.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                  <div className="border-t border-border/40 mt-2 pt-2">
                    <Link
                      to="/work"
                      className="block px-4 py-2 text-sm tracking-wide hover:text-accent transition-colors"
                    >
                      Show All
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            <NavLink to="/hej-mamma">Hej Mamma</NavLink>
            <NavLink to="/brollopsfotograf">Bröllop</NavLink>
            <NavLink to="/about">About</NavLink>
          </div>

          {/* Mobile Menu */}
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger className="md:hidden ml-auto">
              <Menu className="h-6 w-6" />
            </SheetTrigger>
            <SheetContent className="bg-background">
              <div className="flex flex-col gap-6 mt-8">
                <Link
                  to="/"
                  className="text-lg tracking-wide hover:text-accent transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Home
                </Link>

                <div className="flex flex-col gap-4">
                  <span className="text-lg tracking-wide">Work</span>
                  <div className="flex flex-col gap-3 pl-4">
                    {workCategories.map((category) => (
                      <Link
                        key={category.path}
                        to={category.path}
                        className="text-base tracking-wide hover:text-accent transition-colors"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {category.name}
                      </Link>
                    ))}
                    <Link
                      to="/work"
                      className="text-base tracking-wide hover:text-accent transition-colors border-t border-border/40 pt-3 mt-1"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Show All
                    </Link>
                  </div>
                </div>

                <Link
                  to="/hej-mamma"
                  className="text-lg tracking-wide hover:text-accent transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Hej Mamma
                </Link>

                <Link
                  to="/brollopsfotograf"
                  className="text-lg tracking-wide hover:text-accent transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Bröllop
                </Link>

                <Link
                  to="/about"
                  className="text-lg tracking-wide hover:text-accent transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  About
                </Link>

                <Link
                  to="/booking"
                  className="text-lg tracking-wide hover:text-accent transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Booking
                </Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
};
export default Navigation;
